import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import { getUserFullName } from "@/utils/formatDisplayName";
import { messagesService } from "@/api/services/messages.service";
import { useMessagesSignalR } from "@/hooks/useMessagesSignalR";
import { parseUtcIso } from "@/utils/dateTime";
import { useToast } from "@/components/common";
import { ChatWindow, EmptyState } from "@/features/client/notifications/components";
import styles from "./SurveyorMessagesPage.module.css";
const SUPPORT_TITLE = "The Defit Group";
const SUPPORT_SUBTITLE = "DEFIT Staff";
export const SurveyorMessagesPage = () => {
    const { user } = useAuth();
    const { info: showInfo, error: showError } = useToast();
    const [threadId, setThreadId] = useState(null);
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [sendingMessage, setSendingMessage] = useState(false);
    const loadThread = useCallback(async () => {
        if (!user?.id)
            return;
        setLoading(true);
        setThreadId(null);
        setMessages([]);
        try {
            const result = await messagesService.getThreadByGeneral(user.id);
            if (result?.thread && Array.isArray(result.messages)) {
                setThreadId(result.thread.id);
                const transformed = result.messages.map((msg) => ({
                    id: msg.id,
                    threadId: msg.threadId,
                    content: msg.content,
                    senderId: msg.senderId,
                    senderName: msg.senderName,
                    senderType: msg.senderType,
                    timestamp: parseUtcIso(msg.createdAt),
                    isRead: msg.isRead,
                }));
                const seen = new Set();
                const deduped = transformed.filter((m) => {
                    if (seen.has(m.id))
                        return false;
                    seen.add(m.id);
                    return true;
                });
                setMessages(deduped);
                const hasUnreadFromStaff = deduped.some((m) => !m.isRead && (m.senderType === "staff" || m.senderType === "client"));
                if (hasUnreadFromStaff) {
                    await messagesService.markAsRead(result.thread.id);
                }
            }
        }
        catch {
            setThreadId(null);
            setMessages([]);
        }
        finally {
            setLoading(false);
        }
    }, [user?.id]);
    useEffect(() => {
        loadThread();
    }, [loadThread]);
    useMessagesSignalR(threadId, {
        onNewMessage: (payload) => {
            if (payload.threadId !== threadId)
                return;
            const msg = payload.message;
            const chatMessage = {
                id: msg.id,
                threadId: msg.threadId,
                content: msg.content,
                senderId: msg.senderId,
                senderName: msg.senderName,
                senderType: msg.senderType,
                timestamp: parseUtcIso(msg.createdAt),
                isRead: msg.isRead,
            };
            setMessages((prev) => {
                if (prev.some((m) => m.id === msg.id))
                    return prev;
                return [...prev, chatMessage];
            });
            if (msg.senderId !== user?.id) {
                showInfo("New message received");
            }
        },
        onConnectionError: () => {
            showError("Real-time updates unavailable");
        },
    });
    const handleSendMessage = async (content) => {
        if (!user?.id || !content.trim())
            return;
        setSendingMessage(true);
        const optimistic = {
            id: `temp-${Date.now()}`,
            threadId: threadId ?? "",
            content: content.trim(),
            senderId: user.id,
            senderName: "You",
            senderType: "surveyor",
            timestamp: new Date(),
            isRead: true,
        };
        setMessages((prev) => [...prev, optimistic]);
        try {
            const response = await messagesService.sendMessage({
                threadId: threadId ?? undefined,
                threadType: "general",
                contactId: user.id,
                assignedcontactId: user.id,
                content: content.trim(),
                senderId: user.id,
                senderName: getUserFullName(user, "You"),
            });
            if (!threadId && response.data?.threadId) {
                setThreadId(response.data.threadId);
            }
            setMessages((prev) => prev.map((m) => m.id === optimistic.id
                ? {
                    ...m,
                    id: response.data.id,
                    threadId: response.data.threadId ?? m.threadId,
                    timestamp: parseUtcIso(response.data.createdAt),
                }
                : m));
        }
        catch {
            setMessages((prev) => prev.filter((m) => m.id !== optimistic.id));
        }
        finally {
            setSendingMessage(false);
        }
    };
    const showChat = Boolean(!loading && user?.id);
    return (_jsxs("div", { className: styles.container, children: [_jsxs("div", { className: styles.sidebar, children: [_jsx("div", { className: styles.sidebarHeader }), _jsxs("div", { className: styles.chatList, children: [_jsx("div", { className: styles.sectionTitle, children: "Support" }), _jsxs("div", { className: `${styles.chatItem} ${showChat ? styles.chatItemActive : ""}`, children: [_jsxs("div", { className: styles.chatAvatar, children: [_jsx("span", { children: "D" }), _jsx("div", { className: styles.verifiedBadge, children: _jsx("svg", { className: styles.checkIcon, viewBox: "0 0 20 20", fill: "currentColor", children: _jsx("path", { fillRule: "evenodd", d: "M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z", clipRule: "evenodd" }) }) })] }), _jsx("div", { className: styles.chatInfo, children: _jsx("div", { className: styles.chatHeader, children: _jsxs("div", { children: [_jsx("div", { className: styles.chatTitle, children: SUPPORT_TITLE }), _jsx("div", { className: styles.chatSubtitle, children: SUPPORT_SUBTITLE })] }) }) })] })] })] }), _jsx("div", { className: styles.mainContent, children: loading ? (_jsx("div", { className: styles.emptyState, children: _jsx("p", { className: styles.emptyStateSubtitle, children: "Loading conversation\u2026" }) })) : showChat ? (_jsx(ChatWindow, { threadId: threadId ?? `surveyor-${user?.id}`, threadTitle: SUPPORT_TITLE, messages: messages, onSendMessage: handleSendMessage, isOnline: true, isVerified: true, currentUserId: user?.id, currentUserName: user?.name || user?.email || "You", sending: sendingMessage })) : (_jsx(EmptyState, { type: "contact" })) })] }));
};
